/**
 * TODAS as constantes de tuning num só lugar — §5.2 da especificação.
 *
 * Duas razões para isso ser um arquivo só, e não valores espalhados:
 * iterar game feel sem caçar número no código, e garantir que host e guest
 * nunca divirjam de valor (o que dessincronizaria a partida em silêncio).
 *
 * Os valores "humanos" ficam em px/s e unidades por segundo, como a spec os
 * descreve. Os derivados por tick já saem em ponto fixo Q8, calculados uma vez
 * na carga do módulo com aritmética IEEE-exata.
 */
import { fp } from './fixed.js';
/** Versão das regras. Incrementar a cada mudança que altere resultado (§5.3). */
export const RULES_VERSION = 8; // 8: co-op com scroll constante e voo vertical livre (§12.2)
// ─── Tempo ────────────────────────────────────────────────────────────────────
export const TICK_HZ = 60;
/** Milissegundos por tick — só para agendar o loop, nunca dentro da simulação. */
export const TICK_MS = 1000 / TICK_HZ;
/** px/s → ponto fixo por tick. Exportada: o gerador converte velocidades sorteadas. */
export function perTick(pixelsPerSecond) {
    return fp(pixelsPerSecond / TICK_HZ);
}
/**
 * τ (segundos) → fração `num/den` percorrida por tick, para `approach()`.
 * Aproxima `1 - e^(-dt/τ)` por `dt/τ`, que a esta escala (dt = 1/60 s) fica
 * dentro de 1% e é inteiro.
 */
function tauToAlpha(tauSeconds) {
    return { num: 1, den: Math.round(tauSeconds * TICK_HZ) };
}
// ─── Tela e grade — §4 ────────────────────────────────────────────────────────
export const SCREEN = {
    WIDTH: 256,
    HEIGHT: 384,
    PLAYFIELD_HEIGHT: 320,
    HUD_HEIGHT: 64,
    TILE: 16,
    GRID_W: 16,
    GRID_H: 24,
    PLAYFIELD_LINES: 20,
};
// ─── Jogador — §7 ─────────────────────────────────────────────────────────────
export const PLAYER = {
    SPRITE_W: 24,
    SPRITE_H: 24,
    HITBOX_W: 12,
    HITBOX_H: 14,
    /** Y base na área de jogo (§4). */
    BASE_Y: 272,
    /** Faixa de deslocamento vertical individual no multiplayer (§7.5). */
    Y_OFFSET_RANGE: 20,
    /**
     * Voo vertical do co-op (§12.2). Em co-op o scroll é CONSTANTE e ↑/↓ movem
     * o próprio avião na tela, em vez de disputar a velocidade do grupo.
     *
     * As margens são em px de TELA, não deslocamento: é assim que se lê "o
     * avião chega a 32 px do topo" sem fazer conta de cabeça, e continua certo
     * se a altura da área de jogo mudar.
     */
    Y_SPEED: 120,
    Y_SPEED_FP: perTick(120),
    Y_FREE_TOP_PX: 32,
    Y_FREE_BOTTOM_PX: 24,
    LATERAL_SPEED: 140,
    LATERAL_SPEED_FP: perTick(140),
    DEATH_FREEZE_MS: 100,
    EXPLOSION_FRAMES: 5,
    EXPLOSION_MS: 400,
    EXPLOSION_TICKS: Math.round((400 / 1000) * TICK_HZ),
    INVULN_TICKS: 2 * TICK_HZ,
    INVULN_BLINK_HZ: 8,
    RESPAWN_DELAY_TICKS_COOP: 3 * TICK_HZ,
    /**
     * O avião fica a 48 px acima da borda inferior da área de jogo
     * (320 − BASE_Y). É o que converte scrollDist em "linha do mundo sob o
     * avião" — a mesma conta no host, no guest e na revalidação.
     */
    WORLD_Y_AHEAD_PX: 48,
};
export const SCROLL = {
    CRUISE: 120,
    BRAKE: 70,
    BOOST: 220,
    CRUISE_FP: perTick(120),
    BRAKE_FP: perTick(70),
    BOOST_FP: perTick(220),
    ALPHA_SOLO: tauToAlpha(0.25),
    ALPHA_COOP: tauToAlpha(0.4),
};
export const BULLET = {
    W: 4,
    H: 8,
    SPEED: 400,
    SPEED_FP: perTick(400),
    COOLDOWN_TICKS: Math.round((180 / 1000) * TICK_HZ),
    MAX_ALIVE_PER_PLAYER: 3,
    POOL_PER_PLAYER: 6,
};
// ─── Combustível — §9 ─────────────────────────────────────────────────────────
/** Unidades de combustível também em Q8: 100 un. = 25600. */
export const FUEL = {
    TANK: fp(100),
    CONSUMPTION_PER_TICK: fp(2.2 / TICK_HZ),
    REFUEL_PER_TICK: fp(25 / TICK_HZ),
    DEPOT_STOCK_COOP: fp(100),
    LOW_WARNING: fp(30),
    STALL_TICKS: Math.round(1.5 * TICK_HZ),
};
// ─── Pontuação e vidas — §11 ──────────────────────────────────────────────────
export const SCORE = {
    SHIP: 30,
    HELI: 60,
    DEPOT_SOLO: 80,
    DEPOT_COOP: 20,
    JET: 100,
    BRIDGE: 500,
    EXTRA_LIFE_EVERY: 10_000,
    /** Easter egg do original (§11). */
    ROLLOVER_DISPLAY: 1_000_000,
};
export const LIVES = {
    SOLO_START: 3,
    SOLO_MAX_RESERVE: 6,
    COOP_POOL_MAX: 9,
    /** Pool inicial por número de jogadores, índice 0 = solo (§10). */
    COOP_POOL_START: [3, 4, 5, 6],
};
// ─── Mundo e navegabilidade — §6 ──────────────────────────────────────────────
export const WORLD = {
    CHUNK_LINES: 24,
    CHUNKS_ALIVE: 3,
    /** Gerar o próximo chunk ao passar desta fração do atual. */
    PREFETCH_NUMERATOR: 1,
    PREFETCH_DENOMINATOR: 2,
    RIVER_MIN_TILES: 4,
    RIVER_MAX_TILES: 12,
    ISLAND_CHANNEL_MIN_TILES: 3,
    ISLAND_WIDTH_TILES: [1, 4],
    ISLAND_LENGTH_LINES: [6, 20],
    /** Variação máxima de margem por linha: curva de 45°, sem degrau impossível. */
    MAX_WIDTH_DELTA_PER_LINE: 1,
    SAFE_STRAIGHT_LINES: 8,
    SAFE_STRAIGHT_MIN_TILES: 8,
    DECO_DENSITY_NUM: 15,
    DECO_DENSITY_DEN: 100,
    MIN_ENTITY_GAP_LINES: 3,
};
// ─── Gerador procedural — §6.1 ────────────────────────────────────────────────
export const GEN = {
    /**
     * Espaçamento da malha do value-noise, em linhas. Os valores da malha são
     * funções puras de (worldSeed, k) — é isso que permite gerar, descartar e
     * REGENERAR chunks fora de ordem no respawn sem varrer o rio desde o início.
     */
    LATTICE_LINES: 16,
    /** Faixa do centro do rio, em tiles (antes do clamp pelas bordas). */
    CENTER_MIN_TILE: 3,
    CENTER_MAX_TILE: 13,
    /** Terra mínima visível em cada margem, em tiles. */
    EDGE_MARGIN_TILES: 1,
    /**
     * Chunk marcado como RETA_SEGURA: 8 linhas de aproximação (blend do perfil
     * natural para o seguro), 8 linhas retas de largura 10, 8 linhas de saída.
     * As pontas do blend coincidem com o perfil natural — a continuidade com os
     * chunks vizinhos fica garantida por construção, não por conserto.
     */
    SAFE_WIDTH_TILES: 10,
    SAFE_BLEND_LINES: 8,
    SAFE_BAND_START: 8,
    SAFE_BAND_LINES: 8,
    /** Respawn ~1 tela atrás da morte (§7.3): 20 linhas = 320 px. */
    RESPAWN_REWIND_LINES: 20,
    /** Linha do chunk seguro onde o avião renasce (meio da banda reta). */
    RESPAWN_SAFE_LINE: 11,
};
// ─── Entidades — §8 ───────────────────────────────────────────────────────────
export const ENTITY = {
    SHIP: { W: 32, H: 16, DRIFT_MIN: 0, DRIFT_MAX: 20 },
    HELI: { W: 24, H: 24, PATROL_MIN: 40, PATROL_MAX: 70 },
    DEPOT: { W: 16, H: 32 },
    JET: {
        W: 32,
        H: 12,
        SPEED_MIN: 260,
        SPEED_MAX: 340,
        WARNING_MS: 300,
        WARNING_TICKS: Math.round((300 / 1000) * TICK_HZ),
        /** Interferência (§12.3): o jato extra entra ~1,5 s após o aviso. */
        EXTERNAL_WARNING_TICKS: Math.round(1.5 * TICK_HZ),
        /** Distância fora da tela de onde o jato parte. */
        OFFSCREEN_PX: 24,
    },
    BRIDGE: { TILE: 16, TOWER_H: 24 },
    /** Entidade fica inerte até entrar na tela (§8). */
    ACTIVATION_Y: -32,
};
export const POOL = {
    SHIPS: 8,
    HELIS: 8,
    DEPOTS: 4,
    JETS_PER_TARGET: 2,
    EXPLOSIONS: 12,
};
// ─── Colocação de entidades no chunk — §6.2 passo 5 ───────────────────────────
export const SPAWN = {
    /** Linhas elegíveis dentro do chunk (folga nas pontas p/ fronteiras limpas). */
    LINE_MIN: 2,
    LINE_MAX: 21,
    /** Tentativas de sorteio de linha antes de desistir de um slot. */
    TRIES_PER_SLOT: 20,
    /** Folga horizontal entre a hitbox da entidade e a margem, em px. */
    X_MARGIN_PX: 4,
    /** Entre navio e helicóptero, chance do navio (sobre 100). */
    SHIP_WEIGHT: 55,
    /**
     * Regra 3 do validador (§6.3): entidade ESTÁTICA não pode estar a menos de
     * 4 linhas depois de um estreitamento ≥ 2 tiles — o jogador precisa de
     * tempo de reação ao sair do funil.
     */
    REACTION_LINES: 4,
    REACTION_NARROWING_TILES: 2,
};
// ─── Ilhas — §6.2/§6.3 ────────────────────────────────────────────────────────
export const ISLAND = {
    WIDTH_MIN_TILES: 1,
    WIDTH_MAX_TILES: 4,
    LENGTH_MIN_LINES: 6,
    LENGTH_MAX_LINES: 20,
    /** Canal mínimo de cada lado da ilha (§6.3). */
    CHANNEL_MIN_TILES: 3,
};
// ─── Ponte — §8/§10 ───────────────────────────────────────────────────────────
export const BRIDGE = {
    /** Uma ponte a cada 6 chunks: nos chunks com index % 6 === 5. */
    EVERY_CHUNKS: 6,
    /** Linha do chunk onde a ponte vive. */
    LINE: 12,
    /** Largura mínima do rio na linha da ponte (regra 4 da §6.3), em tiles. */
    MIN_WIDTH_TILES: 6,
    /** Altura da hitbox da barreira, em px. */
    H: 16,
};
/** O chunk `index` hospeda uma ponte? Pura — usada por gerador e sim. */
export function isBridgeChunk(index) {
    return index % BRIDGE.EVERY_CHUNKS === BRIDGE.EVERY_CHUNKS - 1;
}
/**
 * Seção POSICIONAL de um chunk — a que governa a geração (§10).
 *
 * É `floor(index / 6)` com cap, e não o contador de pontes destruídas do HUD:
 * a dificuldade precisa ser função pura da posição para o mundo continuar
 * idêntico em host, guest e revalidação, independentemente de quando cada um
 * gera o chunk.
 */
export function sectionOfChunk(index) {
    const section = (index / BRIDGE.EVERY_CHUNKS) | 0;
    return section > DIFFICULTY.CAP_SECTION ? DIFFICULTY.CAP_SECTION : section;
}
// ─── Dificuldade — §10 ────────────────────────────────────────────────────────
export const DIFFICULTY = {
    SECTION_CHUNKS: 6,
    /** Todas as curvas saturam aqui. */
    CAP_SECTION: 12,
    ENTITY_BUDGET_BASE: 4,
    ENTITY_BUDGET_GROWTH_NUM: 12,
    ENTITY_BUDGET_GROWTH_DEN: 100,
    ENTITY_BUDGET_MAX: 10,
    WIDTH_BASE_TILES: 10,
    WIDTH_DECAY_NUM: 4,
    WIDTH_DECAY_DEN: 10,
    WIDTH_FLOOR_TILES: 6,
    ISLAND_PROB_BASE_NUM: 10,
    ISLAND_PROB_GROWTH_NUM: 5,
    ISLAND_PROB_DEN: 100,
    ISLAND_PROB_MAX_NUM: 45,
    JET_FROM_SECTION: 3,
    JET_FREQ_BASE_NUM: 3,
    JET_FREQ_GROWTH_NUM: 1,
    JET_FREQ_DEN: 10,
    JET_FREQ_MAX_NUM: 12,
    HELI_SPEED_PER_SECTION: 4,
    HELI_SPEED_MAX_BONUS: 32,
    /** Escala por nº de jogadores, índice 0 = solo (§10). Percentuais inteiros. */
    BUDGET_BY_PLAYERS_PCT: [100, 150, 190, 220],
    MIN_WIDTH_BY_PLAYERS: [4, 4, 5, 6],
    DEPOT_DENSITY_BY_PLAYERS_PCT: [100, 115, 130, 130],
};
// ─── Replay e revalidação — §14 ───────────────────────────────────────────────
/**
 * Teto de duração de uma run enviada para revalidação.
 *
 * 2 horas a 60 Hz. Existe para limitar o trabalho do servidor: sem teto, um
 * payload forjado com bilhões de ticks vira negação de serviço contra uma VM
 * que também roda o fluxo de pagamentos (§20).
 */
export const MAX_REPLAY_TICKS = 2 * 60 * 60 * TICK_HZ;
export const REPLAY = {
    MAX_TICKS: MAX_REPLAY_TICKS,
    /**
     * Teto de payload aceito pela API.
     *
     * A codificação por repetição gasta 4 bytes por troca de input. Uma run
     * normal (direção segurada, autofire) fica em poucos KB; o pior caso real
     * — jogador alternando input a cada tick — chega perto de 2 bytes por tick.
     * 512 KB cobre o pior caso de uma run de 2 h com folga e ainda é pequeno o
     * bastante para não pesar no upload nem no banco.
     */
    MAX_BYTES: 512 * 1024,
    BYTES_PER_RUN: 4,
};
// ─── Rede — §13 ───────────────────────────────────────────────────────────────
export const NET = {
    STATE_HZ: 20,
    PROGRESS_HZ: 10,
    INTERP_BUFFER_MS: 100,
    RTT_TARGET_MS: 80,
    RTT_PLAYABLE_MS: 150,
    RTC_OPEN_TIMEOUT_MS: 8000,
    ROOM_CODE_LENGTH: 5,
    /**
     * Alfabeto sem ambiguidade visual (§12.4).
     *
     * Tirar I/O/0/1 — o padrão herdado do Pixel Paddle Duel — resolve a
     * ambiguidade CLÁSSICA, mas foi decidido no abstrato, e cada fonte tem as
     * suas. Medindo o IoU dos bitmaps no atlas de 32px (que é o tamanho em que
     * o código aparece), a Pixelify Sans junta os glifos densos e redondos num
     * bolo só: B/8 em 95,7%, 6/8 em 92,8%, S/6 em 92,4%, Z/2 em 92,4%. Para
     * calibrar: a mediana dos 496 pares é 48,9% e o p90 é 77%, então essa faixa
     * é a cauda extrema, não ruído da métrica.
     *
     * Removidos B C P S Z 3 6 8 — a menor remoção que derruba TODO par acima de
     * 85%. Sobram 24 caracteres, 8,0 milhões de códigos de 5 posições, folga de
     * sobra para sala efêmera.
     *
     * Código de sala é lido em voz alta e digitado à mão: aqui parecido é o
     * mesmo que errado.
     */
    ROOM_CODE_ALPHABET: 'ADEFGHJKLMNQRTUVWXY24579',
    MAX_GUESTS: 3,
    MAX_MSG_BYTES: 16 * 1024,
    HEARTBEAT_MS: 25_000,
    RECONNECT_GRACE_TICKS: 30 * TICK_HZ,
};
//# sourceMappingURL=config.js.map